/**
 * Offline Action Queue
 * 
 * IndexedDB-based queue for storing mutations when offline.
 * Actions are processed by SyncManager when connectivity is restored.
 */
import { get, set, del } from 'idb-keyval';

// Supported offline action types
export type OfflineActionType = 
  | 'UPDATE_INSTALLATION_STATUS'
  | 'UPDATE_LOAD_STATUS';

export type ActionStatus = 'pending' | 'processing' | 'failed';

export interface PendingAction {
  id: string;
  type: OfflineActionType;
  payload: {
    installationId?: string;
    loadId?: string;
    status?: string;
    notes?: string;
    updatedAt: string;      // Original updated_at for conflict detection
    orderId?: string;       // For SMS notifications
  };
  timestamp: number;        // Creation time
  retries: number;          // Retry counter
  lastError?: string;       // Last error message
  actionStatus: ActionStatus;
}

const QUEUE_KEY = 'stoneflow-action-queue';
export const MAX_QUEUE_SIZE = 100;
export const MAX_RETRIES = 5;

/**
 * Generate a unique ID for actions
 */
function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Get all actions from the queue
 */
export async function getAllActions(): Promise<PendingAction[]> {
  try {
    const queue = await get<PendingAction[]>(QUEUE_KEY);
    return queue || [];
  } catch (error) {
    console.error('[ActionQueue] Failed to get actions:', error);
    return [];
  }
}

/**
 * Get only pending actions (not failed)
 */
export async function getPendingActions(): Promise<PendingAction[]> {
  const actions = await getAllActions();
  return actions.filter(a => a.actionStatus === 'pending' || a.actionStatus === 'processing');
}

/**
 * Get failed actions
 */
export async function getFailedActions(): Promise<PendingAction[]> {
  const actions = await getAllActions();
  return actions.filter(a => a.actionStatus === 'failed');
}

/**
 * Get the count of pending actions
 */
export async function getPendingCount(): Promise<number> {
  const pending = await getPendingActions();
  return pending.length;
}

/**
 * Get the count of failed actions
 */
export async function getFailedCount(): Promise<number> {
  const failed = await getFailedActions();
  return failed.length;
}

/**
 * Add an action to the queue
 */
export async function queueAction(
  action: Omit<PendingAction, 'id' | 'timestamp' | 'retries' | 'actionStatus'>
): Promise<PendingAction> {
  const queue = await getAllActions();
  
  // Check queue size limit
  const pendingCount = queue.filter(a => a.actionStatus !== 'failed').length;
  if (pendingCount >= MAX_QUEUE_SIZE) {
    throw new Error('Liian monta odottavaa toimintoa. Yhdistä verkkoon synkronoidaksesi.');
  }
  
  const newAction: PendingAction = {
    ...action,
    id: generateId(),
    timestamp: Date.now(),
    retries: 0,
    actionStatus: 'pending',
  };
  
  queue.push(newAction);
  await set(QUEUE_KEY, queue);
  
  console.info('[ActionQueue] Action queued:', newAction.type, newAction.id);
  return newAction;
}

/**
 * Update an existing action in the queue
 */
export async function updateAction(action: PendingAction): Promise<void> {
  const queue = await getAllActions();
  const index = queue.findIndex(a => a.id === action.id);
  
  if (index === -1) {
    console.warn('[ActionQueue] Action not found for update:', action.id);
    return;
  }
  
  queue[index] = action;
  await set(QUEUE_KEY, queue);
}

/**
 * Remove a successfully processed action from the queue
 */
export async function removeAction(actionId: string): Promise<void> {
  const queue = await getAllActions();
  const filtered = queue.filter(a => a.id !== actionId);
  await set(QUEUE_KEY, filtered);
  console.info('[ActionQueue] Action removed:', actionId);
}

/**
 * Mark an action as processing
 */
export async function markActionProcessing(actionId: string): Promise<void> {
  const queue = await getAllActions();
  const action = queue.find(a => a.id === actionId);
  
  if (action) {
    action.actionStatus = 'processing';
    await set(QUEUE_KEY, queue);
  }
}

/**
 * Mark an action as failed after max retries
 */
export async function markActionFailed(actionId: string, error: string): Promise<void> {
  const queue = await getAllActions();
  const action = queue.find(a => a.id === actionId);
  
  if (action) {
    action.actionStatus = 'failed';
    action.lastError = error;
    await set(QUEUE_KEY, queue);
    console.warn('[ActionQueue] Action marked as failed:', actionId, error);
  }
}

/**
 * Increment retry count and optionally mark as failed
 */
export async function incrementRetry(actionId: string, error: string): Promise<boolean> {
  const queue = await getAllActions();
  const action = queue.find(a => a.id === actionId);
  
  if (!action) return false;
  
  action.retries++;
  action.lastError = error;
  
  if (action.retries >= MAX_RETRIES) {
    action.actionStatus = 'failed';
    console.warn('[ActionQueue] Action failed after max retries:', actionId);
  } else {
    action.actionStatus = 'pending'; // Reset to pending for retry
  }
  
  await set(QUEUE_KEY, queue);
  return action.retries < MAX_RETRIES;
}

/**
 * Retry a failed action - reset status to pending
 */
export async function retryAction(actionId: string): Promise<void> {
  const queue = await getAllActions();
  const action = queue.find(a => a.id === actionId);
  
  if (action && action.actionStatus === 'failed') {
    action.actionStatus = 'pending';
    action.retries = 0;
    action.lastError = undefined;
    await set(QUEUE_KEY, queue);
    console.info('[ActionQueue] Action reset for retry:', actionId);
  }
}

/**
 * Discard a failed action
 */
export async function discardAction(actionId: string): Promise<void> {
  await removeAction(actionId);
  console.info('[ActionQueue] Action discarded:', actionId);
}

/**
 * Clear all actions (use on logout)
 */
export async function clearActionQueue(): Promise<void> {
  await del(QUEUE_KEY);
  console.info('[ActionQueue] Queue cleared');
}

/**
 * Get human-readable description of an action
 */
export function formatActionDescription(action: PendingAction): string {
  switch (action.type) {
    case 'UPDATE_INSTALLATION_STATUS':
      return `Asennuksen tila: ${action.payload.status}`;
    case 'UPDATE_LOAD_STATUS':
      return `Kuorman tila: ${action.payload.status}`;
    default:
      return 'Tuntematon toiminto';
  }
}
